import {APP_BASE_HREF, CommonModule} from '@angular/common';
import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import { Injector, NgModule, inject, provideAppInitializer } from "@angular/core";
import {ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {Router} from '@angular/router';
import {TranslocoModule} from '@ngneat/transloco';
import {tap} from 'rxjs';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import { provideCoreWdk } from "@unx/wdk/core";
import { TRIO_WIDGET_REGISTRY_PUBLISH, provideTrioPublish } from "@unx/theme-2";
import {LbwRouteLoaderService, routeBuilderFactory} from "@unx/wdk/page-builder";
import {
    BRAND_SETTINGS_TOKEN,
    ENVIRONMENT_TOKEN,
    ROUTE_API_PATH_TOKEN,
    WIDGET_REGISTRY_TOKEN
} from "@unx/wdk/shared/tokens";
import {API_CONTEXT_PATH_PUBLISH} from "@unx/wdk/shared/types";
import {LobuBrandSettingsService} from "./brand-settings.service";
import {AnalyticsService, IUnxBrandSettingsService} from "@unx/wdk/shared/services";
import { LbwBrandSettingsModel } from '@unx/wdk/shared/model';

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        CommonModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        TranslocoModule,
    ],
    providers: [
        provideCoreWdk(),
        provideTrioPublish(),
        // GA4: gtag config + SPA page_view tracking'i bootstrap'ta başlatır.
        provideAppInitializer(() => inject(AnalyticsService).init()),
        provideAppInitializer(() => {
          const routeLoader = inject(LbwRouteLoaderService);
          const router = inject(Router);
          const injector = inject(Injector);
          return routeLoader.getRoutes().pipe(
            tap(() => {
              router.resetConfig(routeBuilderFactory(routeLoader, injector));
            })
          );
        }),
        {
            provide: WIDGET_REGISTRY_TOKEN,
            useValue: TRIO_WIDGET_REGISTRY_PUBLISH,
        },
        {
            provide: IUnxBrandSettingsService,
            useExisting: LobuBrandSettingsService,
        },
        {
          provide: ROUTE_API_PATH_TOKEN,
          // HTTP fallback path when window.unxRouteResponseData is unset.
          // Keep in sync with the bootstrap script in index.html: the active
          // locale is the full ISO tag (`en-US`), matching the init.{iso}.json
          // files emitted by the publish pipeline.
          useFactory: () => {
            const supported: string[] = ["en-US","es-ES","pt-BR","tr-TR","ar-SA","hi-IN","pt-PT","ja-JP","de-DE","fr-FR"];
            const fallback = 'en-US';
            const iso = /^[a-z]{2,3}-[A-Z]{2,3}$/;
            let code: string | null = null;
            try {
              const stored = typeof localStorage !== 'undefined'
                ? localStorage.getItem('selectedLanguageCode')
                : null;
              if (stored && iso.test(stored)) {
                code = stored;
              }
            } catch (e) {}
            if (!code || supported.indexOf(code) < 0) {
              const match = typeof document !== 'undefined'
                ? document.cookie.match(/Suggested-Lang=([^;]+)/)
                : null;
              if (match && iso.test(match[1])) {
                code = match[1];
              }
            }
            if (!code || supported.indexOf(code) < 0) {
              code = fallback;
            }
            return '/ebd14d06-8d22-49db-a602-7d0eb5b59e67/init.' + code + '.json';
          },
        },
        {
            provide: API_CONTEXT_PATH_PUBLISH,
            useValue: 'https://betonda.com/api'
        },
        {
            provide: ENVIRONMENT_TOKEN,
            useValue: 'publish',
        },
        {
            provide: APP_BASE_HREF,
            useValue: '/'
        },
        {
            provide: BRAND_SETTINGS_TOKEN,
            useValue: {"id":"b8e4c2a1-9d0f-4b7e-8c3a-1f2e3d4c5b6a","createdBy":"408","updatedAt":"2026-08-06T08:43:44.075Z","updatedBy":"408","_trash":"00000000-0000-0000-0000-000000000000","_search":"b8e4c2a1-9d0f-4b7e-8c3a-1f2e3d4c5b6a betonda","_release":2,"name":"betonda","brand":"betonda","deployments":{"sandbox":"","prod":""},"i18n":{"default":"en-US","available":["en-us","pt-br","es-es"],"editable":["en-us","pt-br","es-es"],"mapped":{}},"images":{"topLogo":"top-logo-url","bottomLogo":"bottom-logo-url"},"_irregulars":[],"__v":0,"title":"Betonda","brandTitle":"Betonda","backgroundImageUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/logo-bg.png","faviconUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/3cf18b5a-237a-4848-8f90-2b17a25cd58f_1782132032.png","logoType":"horizontal","logoUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/6244943e-0d07-434c-bf41-ebae7eace71b_1782130424.png","cdnUrl":"https://d1d2i23lrx0rwv.cloudfront.net","gifEnabled":false,"gifUrl":"","animationType":"image","cloudflareInsightToken":"2bf601682d484f67acce2f1ff55d58fb","footerSealCode":"anj-218a9a5c-7aba-4b45-baf4-2228ea2ebd2b","footerSealDirectiveCode":"anj_218a9a5c_7aba_4b45_baf4_2228ea2ebd2b.init()","footerSealText":"Betonda.com is owned and operated by Unexpected Ltd., registration number 15651, registered address Hamchako, Mustamudu, Autonomous Island of Anjouan, Union of Comoros. Contact us at support@Betonda.com. Betonda.com is licensed and regulated by the Government of the Autonomous Island of Anjouan, Union of Comoros and operates under License No. ALSI-042402014-Fl4. Betonda.com has passed all regulatory compliance and is legally authorized to conduct gaming operations for any and all games of chance and wagering.","headerSealScript":"https://218a9a5c-7aba-4b45-baf4-2228ea2ebd2b.snippet.anjcdn.org/anj-seal.js","apiContextPath":"https://betonda.com/api","noImageUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/favicon-2.png","hasLiveChat":false,"dark":{"logoUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/logo.svg"},"light":{"logoUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/logo-dark.svg"},"metadataCasinoEndpoint":"http://metadata-casino.unexpect.tech","authFlowPopup":true,"emailVerificationRequiredForWithdraw":true,"kyc1Required":true,"kycRequired":true,"withdrawKycMinLevel":2,"loyalty":false,"emailVerificationRequiredForRegister":true,"needPhoneNumberForRegister":false,"discordLink":"","telegramLink":"","twitterLink":"","themeId":"f21cd1cb-6c05-45a5-bbc3-17a9d29d762a","themeName":"theme-2","colorPalette":"unx-theme-2 betonda-theme","themeVersionNumber":"0.0.130","wdkVersionNumber":"0.0.130","widgetModuleName":"TrioPublishModule","widgetRegistryName":"TRIO_WIDGET_REGISTRY","widgetRegistryPublishName":"TRIO_WIDGET_REGISTRY_PUBLISH","sportsBookProvider":"betby","chatProvider":"","mobileLogoUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/05406f7a-e10c-4237-8f83-ee6fb5a7f4c3_1782132037.png","betbyBrandId":"2465880529438904329","betbyBrandTheme":"betby-demo-yellow-dark-tile","betbyScriptUrl":"https://ui.invisiblesport.com/bt-renderer.min.js","ticketsEnabled":true,"buyCryptoWidgetEnabled":false,"buyProviders":["onramper"],"onramperPublicApiKey":"pk_prod_01KJ4XA4QSH5Y9VW27Q9EN096C","onramperWidgetUrl":"https://buy.onramper.com","socials":[{"icon":"instagram","urls":{"en-US":"https://www.instagram.com/betonda_","es-ES":"https://www.instagram.com/betondalatam","pt-BR":"https://www.instagram.com/betonda_"}},{"icon":"twitter","urls":{"en-US":"https://x.com/Betonda_","es-ES":"https://x.com/BetOndaLATAM","pt-BR":"https://x.com/Betonda_"}},{"icon":"telegram","urls":{"en-US":"https://t.me/+enIAVw26TPEwMDU0","es-ES":"https://t.me/+enIAVw26TPEwMDU0","pt-BR":"https://t.me/+enIAVw26TPEwMDU0"}}],"metaTagThemeColor":"#1e2126","chatEnabled":false,"gameNoImageUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/d5e9eba1-42f2-4c45-9d3b-0a451b36549e_1782132024.png","gameNoImageUrls":{"en-US":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/d5e9eba1-42f2-4c45-9d3b-0a451b36549e_1782132024.png","es-ES":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/246d06cc-a151-4584-83d7-7b6c9dda2a4f_1778667380.png","pt-BR":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/d7761c04-75cb-4ad5-b5be-f128bc5987d2_1778667395.png"},"registerBackgroundUrl":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/07099204-bc39-4a03-9e8e-d4ad3341cc6b_1778159984.png","registerBackgroundUrls":{"en-US":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/07099204-bc39-4a03-9e8e-d4ad3341cc6b_1778159984.png","es-ES":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/07150923-ded8-4e32-8dca-7f3dfefae81f_1778667365.png","pt-BR":"https://d1d2i23lrx0rwv.cloudfront.net/int/betonda/u/7a27d582-9ad3-4daa-b89e-17c867f56869_1778667389.png"},"widgetRegistry":{},"fiatWidgetEnabled":false,"chatProviderScriptUrl":"","fcwBanner":false,"useTopLoadingBar":true,"fiatDepositProviders":["payadmit"],"fiatWithdrawProviders":["payadmit"],"fwcMatchSlider":true} as unknown as LbwBrandSettingsModel,
        },
        provideHttpClient(withInterceptorsFromDi()),
    ],
    
})
export class AppModule {
}
